import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { ArrowDownIcon } from '@src/components/common/icon';

const DropdownWrapper = styled.div`
	display: inline-block;
	position: relative;
`;

const SelectedOption = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 8px 8px 8px 12px;
	border-radius: 4px;
	border: 1px solid ${props => (props.isError ? ' #D0021B' : '#DDD')};
	color: ${props => !props.isDisabled && '#333333'};
	background: ${props => (props.isDisabled ? '#ECECEC' : '#FFF')};
	cursor: ${props => !props.isDisabled && 'pointer'};
`;

const SelectedText = styled.div`
	display: -webkit-box;
	text-overflow: ellipsis;
	overflow: hidden;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
`;

const ExpandIcon = styled.div`
	display: inline-flex;
	align-items: center;
	margin-left: 4px;
	transform: rotate(${props => (props.expand ? 180 : 0)}deg);
	transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DropdownOptions = styled.div.attrs({ className: 'options' })`
	position: absolute;
	width: 100%;
	max-height: 320px;
	border-radius: 4px;
	border: 1px solid #DDD;
	margin-top: 4px;
	background: #fff;
	overflow-y: auto;
	z-index: 100;
`;

const DropdownOption = styled.div`
	padding: 8px 12px;
	color: ${props => (props.isSelected ? props.theme.colors.primary : '#333')};
	cursor: ${props => !props.isGrayOut && 'pointer'};

	&:hover {
		${props => !props.isGrayOut && `
			color: #fff;
			background: ${props.theme.colors.primary};
		`};
	}
`;

const DropdownOptionText = styled.div`
	${props => props.isGrayOut && 'opacity: 0.5'}
	${props => props.ellipsisOption && `
		display: -webkit-box;
		text-overflow: ellipsis;
		overflow: hidden;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	`}
`;

interface IOption {
	type: number | string;
	text: number | string | JSX.Element;
	isSelected: boolean;
	isGrayOut?: boolean;
}

interface IDropdown {
	className?: string;
	isDisabled?: boolean;
	isError?: boolean;
	selectedOption: number | string;
	options: IOption[];
	ellipsisOption?: boolean;
	handleSelectOption: (string) => void;
	component?: (isExpand: boolean) => JSX.Element;
}

const Dropdown = ({
	className, isDisabled = false, isError = false, selectedOption, options, ellipsisOption = true, handleSelectOption,
	component,
}: IDropdown): JSX.Element => {
	const [isExpand, setIsExpand] = useState(false);
	const dropdownRef = useRef();

	const handleToggleArrow = () => { setIsExpand(prev => !prev); };
	const handleSelect = type => {
		handleSelectOption(type);
		setIsExpand(false);
	};

	useEffect(() => {
		const handleClickOutside = e => {
			const dropdownEle = dropdownRef.current || null;

			if (dropdownEle && !dropdownEle.contains(e.target)) {
				setIsExpand(false);
			}
		};

		window.addEventListener('mousedown', handleClickOutside);

		return () => {
			window.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<DropdownWrapper className={className} ref={dropdownRef}>
			{
				component ? (
					// eslint-disable-next-line jsx-a11y/click-events-have-key-events
					<div onClick={isDisabled ? null : handleToggleArrow}>
						{component(isExpand)}
					</div>
				) : (
					<SelectedOption
						isDisabled={isDisabled}
						isError={isError}
						onClick={isDisabled ? null : handleToggleArrow}
					>
						<SelectedText>{selectedOption}</SelectedText>
						<ExpandIcon expand={isExpand}>
							<ArrowDownIcon />
						</ExpandIcon>
					</SelectedOption>
				)
			}
			{isExpand && (
				<DropdownOptions>
					{options.map(option => (
						<DropdownOption
							key={option.type}
							isSelected={option.isSelected}
							isGrayOut={option.isGrayOut}
							onClick={option.isGrayOut ? null : () => handleSelect(option.type)}
						>
							<DropdownOptionText ellipsisOption={ellipsisOption} isGrayOut={option.isGrayOut}>
								{option.text}
							</DropdownOptionText>
						</DropdownOption>
					))}
				</DropdownOptions>
			)}
		</DropdownWrapper>
	);
};

export default Dropdown;
